<template>
  <multiselect v-model="selectedValue" :options="items" label="label" track-by="id" class="po-selector"
    select-label="" deselect-label="" selected-label="" :loading="loading" :placeholder="placeholder" :allow-empty="!required" :disabled="disabled"
    :internal-search="searcheable" @search-change="onSearch" @open="opened" @close="closed" :multiple="multiple" :hide-selected="multiple" :close-on-select="!multiple">

    <span slot="noResult">
      <span v-t="'general.noResult'" />
    </span>
    <span slot="noOptions">
      <span v-if="search && !loading" v-t="'general.enterSearchText'"></span>
    </span>
    <li v-if="overflowText" slot="afterList" class="multiselect__element">
      <span class="multiselect__option">
        <b>{{ overflowText }}</b>
      </span>
    </li>
    <div slot="caret" class="po-selector__caret">
      <wo-icon icon="chevron-down" />
    </div>
    <a slot="tag" slot-scope="{option, remove}" @click="remove(option)" class="po-selector__tag">
      <wo-pill class="wo-pill--sm" :mod-closable="true" >{{ option.label }}</wo-pill>
    </a>
  </multiselect>
</template>
<script>

import _ from 'lodash'
import { Entities } from '@/model/util'

import Multiselect from 'vue-multiselect'

export default {
  name: 'PoSelector',
  components: { Multiselect },
  props: {
    value: undefined,
    placeholderKey: String,
    options: [Array, Object],
    search: Function,
    required: Boolean,
    disabled: Boolean,
    multiple: Boolean,
    labelFactory: Function
  },
  data () {
    return {
      selectedValue: undefined,
      searchItems: [],
      loading: false,
      overflow: undefined
    }
  },
  computed: {
    items () {
      return this.search ? this.searchItems : _.map(this.options, this.createItem)
    },
    placeholder () {
      return this.placeholderKey ? this.$t(this.placeholderKey) : this.defaultPlaceholder
    },
    defaultPlaceholder () {
      return this.$t(this.search ? 'general.searchTextPlaceholder' : 'general.selectPlaceholder')
    },
    searcheable () {
      return !this.search
    },
    overflowText () {
      return this.overflow > 0 && this.$t('general.moreResults', { count: this.overflow })
    }
  },
  watch: {
    value () {
      this.updateValue()
    },
    items () {
      this.updateValue()
    },
    selectedValue () {
      this.emitInput()
    }
  },
  created () {
    this.updateValue()
  },
  methods: {
    createItem (option, index) {
      const fixedLabel = (this.labelFactory && this.labelFactory(option, index)) || this.getOptionLabel(option)
      return {
        id: option.id || index,
        value: option,
        label: fixedLabel || this.$t(option.labelKey)
      }
    },
    updateValue () {
      if (!_.isEmpty(this.items)) {
        if (this.multiple) {
          this.selectedValue = this.value && _.filter(_.map(this.value, this.getItem))
        } else {
          this.selectedValue = this.getItem(this.value)
        }
      }
    },
    getItem (value) {
      return value && _.find(this.items, o => Entities.areEqual(o.value, value))
    },
    getOptionLabel (option) {
      return _.isString(option) ? option : option.name || option.label || option.display_name
    },
    emitInput () {
      if (this.multiple) {
        const newValue = this.selectedValue && _.map(this.selectedValue, v => v && v.value)
        if (!Entities.areAllEqual(this.value, newValue)) {
          this.$emit('input', newValue)
        }
      } else if (!Entities.areEqual(this.selectedValue, this.value)) {
        this.$emit('input', this.selectedValue && this.selectedValue.value)
      }
    },
    onSearch: _.debounce(function (text) { this.doSearch(text) }, 500),
    doSearch (text) {
      if (this.search && !this.loading && this.open) {
        this.loading = true
        const result = this.search(text)
        Promise.resolve(result)
          .then(this.handleResult)
          .finally(() => { this.loading = false })
      }
    },
    opened () {
      this.open = true
    },
    closed () {
      this.open = false
    },
    handleResult (options) {
      this.overflow = Entities.getOverflow(options)
      this.searchItems = _.map(Entities.items(options), this.createItem)
    }
  }
}
</script>
<style lang="scss">

$gray: #92a7b3;
$foreground: #1b252f;
$po-input-height: 44px;
$highlight-color: #00a3d3;
$border-radius: 3px;

.multiselect.po-selector {
  min-height: $po-input-height;
  margin: .625rem 0 1.5rem;

  &.multiselect--above, &.multiselect--active {
    .multiselect__tags {
      border-radius: $border-radius;
    }
  }

  &.multiselect--active {
    .multiselect__tags {
      box-shadow: 0 0 0 2px #fff, 0 0 0 5px rgba(0, 163, 211, 0.5);
      transition: box-shadow .15s linear;
      outline: none;
    }
  }

  .wo-properties & {
    margin: 0;
  }

  .po-selector__caret {
    position: absolute;
    right: 1px;
    top: 1px;
    padding-top: 12px;
    padding-left: 12px;
    width: $po-input-height;
    height: $po-input-height;
    color: $highlight-color;
  }

  .multiselect__content-wrapper {
    border-color: $gray;
    border-radius: 0;
  }

  .multiselect__tags {
    padding-left: 10px;
    border-color: $gray;
    border-radius: $border-radius;
    min-height: $po-input-height;
  }

  .multiselect__input, .multiselect__single, .multiselect__placeholder {
    line-height: 1.5;
    padding: 0 15px;
    font-size: 16px;
    font-weight: 400;
  }

  .multiselect__option {
    padding: 15px;
  }

  .multiselect__spinner {
    top: $border-radius;
    right: $border-radius;
    min-height: $po-input-height - 2 * $border-radius;

    &:after, &:before {
      border-top-color: $highlight-color;
    }
  }

  .multiselect__option--selected {
    background-color: transparent;
    color: $foreground;
  }

  .multiselect__option--highlight {
    background-color: #ecf1f5;
    color: $foreground;
  }

  .po-selector__tag {
    display: inline-block;
    cursor: pointer;
    margin-right: 0.5em;
    max-width: 100%;

    &:hover , &:active, &:focus {
      text-decoration: none;
    }
    .wo-pill {
      margin-bottom: 0.35em;
      max-width: 100%;
      text-overflow: ellipsis;
    }
  }

  .theme-alt & {
    .multiselect__tags {
      background-color: transparent;
      border-color: #bdcbd3;
      border-width: 2px;
      border-radius: 0;
    }

    &.multiselect--active {
      .multiselect__tags {
        box-shadow: 0 0 0 2px transparentize(#d80017, .3);
      }
    }

    .po-selector__caret {
      color: #cb0008;
    }

    input {
      background-color: transparent;
    }

    .multiselect__input, .multiselect__single {
      background-color: transparent;
      color:  #ffffff;

      &::placeholder {
        color: #bdcbd3;
      }
    }

    .multiselect__placeholder {
      background-color: transparent;
      color: #bdcbd3;
    }

    .multiselect__option--selected {
      background-color: transparent;
      color: $foreground;
    }

    .multiselect__option--highlight {
      background-color: #cb0008;
      color: #ffffff;
    }
  }

  .wo-card & {
    .multiselect__single {
      color: #000000;
    }
  }
}
</style>
